import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Zoom,
  Fade,
  Alert,
  Divider,
  Button,
} from '@mui/material';
import {
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Close as CloseIcon,
  ContentCopy as ContentCopyIcon,
  GridView as GridViewIcon,
  ViewComfy as ViewComfyIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import {
  convertToNormalized,
  normalizedToScreen,
  isValidCoordinates,
  type Coordinates,
} from '../../../utils/coordinateTransform';
import PDFCanvasViewer, { PageRenderInfo } from './PDFCanvasViewer';

interface PDFOverlayViewerProps {
  pdfUrl: string;
  extractedData: any;
  onFieldClick?: (field: string, value: any) => void;
  annotations?: Array<{
    id: string;
    field_name: string;
    coordinates: { x: number; y: number; width: number; height: number };
    page_number: number;
    type: string;
    priority: string;
  }>;
}

interface FieldOverlay {
  field: string;
  value: any;
  type: string;
  color: string;
  confidence?: number;
  position?: {
    x: number;
    y: number;
    width: number;
    height: number;
    page?: number;
  };
}

const PDFOverlayViewerV2: React.FC<PDFOverlayViewerProps> = ({
  pdfUrl,
  extractedData,
  onFieldClick,
  annotations = [],
}) => {
  const [scale, setScale] = useState(1);
  const [showOverlays, setShowOverlays] = useState(true);
  const [selectedField, setSelectedField] = useState<string | null>(null);
  const [overlays, setOverlays] = useState<FieldOverlay[]>([]);
  const [hoveredField, setHoveredField] = useState<string | null>(null);

  // NEW: Real page dimensions from backend
  const [backendPageDimensions, setBackendPageDimensions] = useState<Map<number, { width: number; height: number }>>(new Map());

  // NEW: Actual render info from react-pdf
  const [pageRenderInfo, setPageRenderInfo] = useState<Map<number, PageRenderInfo>>(new Map());

  // Marker mode states
  const [displayMode, setDisplayMode] = useState<'markers' | 'overlays'>('markers');
  const [selectedMarker, setSelectedMarker] = useState<FieldOverlay | null>(null);
  const [popupPosition, setPopupPosition] = useState<{x: number, y: number} | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const overlayContainerRef = useRef<HTMLDivElement>(null);

  // Marker constants
  const MARKER_SIZE = 10;

  // Utility function to flatten nested objects (e.g., ATLAS customs data)
  const flattenObject = (obj: any, prefix = ''): Array<{key: string, value: any}> => {
    const flattened: Array<{key: string, value: any}> = [];

    Object.keys(obj).forEach(key => {
      // Skip metadata and internal fields
      // Internal fields start with _ (e.g., _compliance_extraction)
      // Metadata fields are system-generated data not meant for annotation
      if (
        key === 'metadata' ||
        key === 'ocr_confidence' ||
        key === 'processing_time_ms' ||
        key.startsWith('_') ||  // Skip all internal fields with _ prefix
        key === 'compliance_extraction' ||  // Skip compliance metadata
        key === 'risk_assessment' ||  // Skip risk assessment metadata
        key === 'sanctions_check' ||  // Skip sanctions metadata
        key === 'check_results'  // Skip check results metadata
      ) {
        return;
      }

      const value = obj[key];
      const newKey = prefix ? `${prefix}.${key}` : key;

      // Skip large arrays (> 10 items) - these are usually metadata like compliance entities
      if (Array.isArray(value) && value.length > 10) {
        return;
      }

      if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
        // Recursively flatten nested objects
        flattened.push(...flattenObject(value, newKey));
      } else if (value !== undefined && value !== null && value !== '') {
        // Leaf node with actual value - add to flattened array
        flattened.push({ key: newKey, value });
      }
    });

    return flattened;
  };

  // Extract page dimensions from backend metadata
  useEffect(() => {
    console.log('🔍 DEBUG: extractedData received:', extractedData);
    console.log('🔍 DEBUG: metadata exists?', !!extractedData?.metadata);
    console.log('🔍 DEBUG: page_dimensions exists?', !!extractedData?.metadata?.page_dimensions);
    console.log('🔍 DEBUG: page_dimensions value:', extractedData?.metadata?.page_dimensions);

    if (extractedData?.metadata?.page_dimensions) {
      const dims = extractedData.metadata.page_dimensions;
      console.log('📐 Backend page dimensions:', dims);

      // Convert array to Map for easy lookup by page number
      const dimMap = new Map<number, { width: number; height: number }>();
      dims.forEach((pageDim: { page: number; width: number; height: number }) => {
        dimMap.set(pageDim.page, { width: pageDim.width, height: pageDim.height });
      });

      setBackendPageDimensions(dimMap);
    } else {
      console.warn('⚠️  No page_dimensions in backend metadata - coordinates may be inaccurate');
    }
  }, [extractedData]);

  // Process extracted data to create overlays with real coordinates
  useEffect(() => {
    if (extractedData) {
      const fieldOverlays: FieldOverlay[] = [];
      let backendCoordinatesCount = 0;
      let ocrCoordinatesCount = 0;
      let fallbackCount = 0;

      // Helper functions for field type and color determination
      const determineFieldType = (fieldName: string): string => {
        const lowerName = fieldName.toLowerCase();
        if (lowerName.includes('date') || lowerName.includes('datum')) return 'date';
        if (lowerName.includes('amount') || lowerName.includes('total') ||
            lowerName.includes('price') || lowerName.includes('sum') || lowerName.includes('betrag')) return 'amount';
        if (lowerName.includes('number') || lowerName.includes('nr') ||
            lowerName.includes('id') || lowerName.includes('nummer')) return 'reference';
        if (lowerName.includes('address') || lowerName.includes('name') || lowerName.includes('ort') ||
            lowerName.includes('strasse') || lowerName.includes('plz') || lowerName.includes('land')) return 'address';
        return 'text';
      };

      const determineFieldColor = (fieldType: string): string => {
        switch (fieldType) {
          case 'date': return 'rgba(156, 39, 176, 0.3)'; // Purple
          case 'amount': return 'rgba(76, 175, 80, 0.3)'; // Green
          case 'reference': return 'rgba(255, 152, 0, 0.3)'; // Orange
          case 'address': return 'rgba(0, 188, 212, 0.3)'; // Cyan
          default: return 'rgba(66, 165, 245, 0.3)'; // Blue
        }
      };

      // Create a Map of field_name → annotation for O(1) lookup
      const annotationMap = new Map<string, any>();
      if (annotations && annotations.length > 0) {
        annotations.forEach(ann => {
          annotationMap.set(ann.field_name, ann);
        });
        console.log(`📍 PDFOverlayViewerV2: Created annotation map with ${annotationMap.size} backend annotations`);
      }

      // Count total fields before filtering
      const countAllFields = (obj: any): number => {
        let count = 0;
        Object.keys(obj).forEach(key => {
          if (key === 'metadata' || key === 'ocr_confidence' || key === 'processing_time_ms') return;
          const value = obj[key];
          if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
            count += countAllFields(value);
          } else if (value !== undefined && value !== null && value !== '') {
            count++;
          }
        });
        return count;
      };
      const totalFieldsBeforeFilter = countAllFields(extractedData);

      // Flatten nested object structure to handle ATLAS customs data
      const flattenedFields = flattenObject(extractedData);

      // Enhanced logging with filter statistics
      console.log('📊 Field Filtering Statistics:');
      console.log(`   └─ Total fields in extractedData: ${totalFieldsBeforeFilter}`);
      console.log(`   └─ After filtering (internal/metadata): ${flattenedFields.length}`);
      console.log(`   └─ Filtered out: ${totalFieldsBeforeFilter - flattenedFields.length} internal/metadata fields`);
      console.log('📋 Sample field names:', flattenedFields.map(f => f.key).slice(0, 10), '...');

      // Debug: Check OCR metadata structure
      if (extractedData.metadata?.word_coordinates) {
        console.log(`📊 OCR word_coordinates available: ${extractedData.metadata.word_coordinates.length} items`);
      } else {
        console.log('⚠️  No OCR word_coordinates in metadata');
      }

      // Common default values that shouldn't be annotated
      const DEFAULT_VALUES = ['EX', 'DE', 'EUR', '11', '1', '0', 'false'];

      flattenedFields.forEach(({ key, value }) => {
        // Filter out common default values
        const stringValue = String(value).trim();
        const isDefaultValue = DEFAULT_VALUES.includes(stringValue) ||
                              stringValue === '' ||
                              stringValue === 'null' ||
                              stringValue === 'undefined';

        // Skip default values unless they have backend annotations
        const annotation = annotationMap.get(key);
        if (isDefaultValue && !annotation) {
          return; // Skip this field
        }

        const type = determineFieldType(key);
        const color = determineFieldColor(type);

        // THREE-TIER COORDINATE PRIORITY SYSTEM:
        let coords: Coordinates | null = null;
        let coordinateSource = '';

        // FIRST PRIORITY: Backend annotation coordinates (most reliable)
        if (annotation && annotation.coordinates) {
          const annCoords = annotation.coordinates;
          // Validate that coordinates are not zero
          if (annCoords.x !== 0 || annCoords.y !== 0 || annCoords.width !== 0 || annCoords.height !== 0) {
            coords = {
              x: annCoords.x,
              y: annCoords.y,
              width: annCoords.width,
              height: annCoords.height,
              page: annotation.page_number || 1
            };
            coordinateSource = 'backend';
            backendCoordinatesCount++;
          }
        }

        // SECOND PRIORITY: OCR word_coordinates (fallback if backend coordinates missing)
        if (!coords && extractedData.metadata?.word_coordinates) {
          const searchText = String(value);
          const wordCoords = extractedData.metadata.word_coordinates;

          for (const wordData of wordCoords) {
            if (wordData.text?.toLowerCase().includes(searchText.toLowerCase())) {
              coords = wordData.bbox;
              coordinateSource = 'ocr';
              ocrCoordinatesCount++;
              break;
            }
          }
        }

        // Transform coordinates or use fallback positioning
        // NOTE: We don't transform here - we transform during rendering with real dimensions
        if (coords && isValidCoordinates(coords)) {
          fieldOverlays.push({
            field: key,
            value: value,
            type: type,
            color: color,
            confidence: extractedData.ocr_confidence || 0.95,
            position: coords // Store PDF coordinates as-is
          });
        } else {
          // THIRD PRIORITY: Fallback grid positioning when no coordinates available
          const page = (annotation && annotation.page_number) ? annotation.page_number : 1;
          const index = fieldOverlays.length;

          // Fallback positions (will be positioned in a grid)
          fieldOverlays.push({
            field: key,
            value: value,
            type: type,
            color: color,
            confidence: extractedData.ocr_confidence || 0.95,
            position: {
              x: 100 + (index % 3) * 220,  // 3 columns
              y: 100 + Math.floor(index / 3) * 55,  // Rows spaced 55px apart
              width: 200,
              height: 40,
              page: page
            }
          });
          fallbackCount++;
        }
      });

      setOverlays(fieldOverlays);

      // Log detailed statistics
      const skippedDefaults = flattenedFields.length - fieldOverlays.length;
      console.log('');
      console.log(`📍 Final Overlay Statistics:`);
      console.log(`   └─ Created ${fieldOverlays.length} markers for user-facing fields`);
      console.log(`   └─ Skipped ${skippedDefaults} default/empty value fields`);
      console.log(`   └─ Coordinate sources:`);
      console.log(`      • Backend annotations: ${backendCoordinatesCount}`);
      console.log(`      • OCR word matching: ${ocrCoordinatesCount}`);
      console.log(`      • Fallback positioning: ${fallbackCount}`);
      console.log(`   └─ Coordinate accuracy: ${Math.round((backendCoordinatesCount + ocrCoordinatesCount) / fieldOverlays.length * 100)}%`);
      console.log(`🔍 DEBUG: showOverlays=${showOverlays}, displayMode=${displayMode}`);
    }
  }, [extractedData, annotations]);

  const handleZoomIn = () => {
    const newScale = Math.min(scale + 0.25, 2);
    setScale(newScale);
  };

  const handleZoomOut = () => {
    const newScale = Math.max(scale - 0.25, 0.5);
    setScale(newScale);
  };

  const handleFieldSelect = (field: string, value: any) => {
    setSelectedField(field);
    if (onFieldClick) {
      onFieldClick(field, value);
    }
  };

  const handleMarkerClick = (overlay: FieldOverlay, event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedMarker(overlay);

    // Calculate popup position relative to container
    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    const containerRect = containerRef.current?.getBoundingClientRect();

    if (containerRect) {
      setPopupPosition({
        x: rect.left - containerRect.left + MARKER_SIZE + 10,
        y: rect.top - containerRect.top - 10
      });
    }
  };

  const handleClosePopup = () => {
    setSelectedMarker(null);
    setPopupPosition(null);
  };

  const getFieldTypeIcon = (type: string) => {
    switch (type) {
      case 'date': return '📅';
      case 'amount': return '💰';
      case 'reference': return '🔢';
      case 'address': return '📍';
      default: return '📝';
    }
  };

  return (
    <Box ref={containerRef} sx={{ position: 'relative', height: '100%', display: 'flex' }}>
      {/* PDF Viewer with Overlay */}
      <Box sx={{ flex: 1, position: 'relative', overflow: 'auto', bgcolor: 'grey.100' }}>
        {/* Control Bar */}
        <Paper
          elevation={2}
          sx={{
            position: 'absolute',
            top: 10,
            right: 10,
            zIndex: 1000,
            p: 1,
            display: 'flex',
            gap: 1,
            bgcolor: 'background.paper',
          }}
        >
          <Tooltip title="Zoom In">
            <IconButton size="small" onClick={handleZoomIn}>
              <ZoomInIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Zoom Out">
            <IconButton size="small" onClick={handleZoomOut}>
              <ZoomOutIcon />
            </IconButton>
          </Tooltip>
          <Chip label={`${Math.round(scale * 100)}%`} size="small" />

          {/* Display mode toggle */}
          <Divider orientation="vertical" flexItem />
          <Tooltip title={displayMode === 'markers' ? "Switch to Overlay Mode" : "Switch to Marker Mode"}>
            <IconButton
              size="small"
              onClick={() => setDisplayMode(displayMode === 'markers' ? 'overlays' : 'markers')}
              color={displayMode === 'markers' ? "primary" : "default"}
            >
              {displayMode === 'markers' ? <GridViewIcon /> : <ViewComfyIcon />}
            </IconButton>
          </Tooltip>
          <Chip
            label={displayMode === 'markers' ? 'Markers' : 'Overlays'}
            size="small"
            color={displayMode === 'markers' ? 'primary' : 'default'}
          />

          <Divider orientation="vertical" flexItem />
          <Tooltip title={showOverlays ? "Hide" : "Show"}>
            <IconButton
              size="small"
              onClick={() => setShowOverlays(!showOverlays)}
              color={showOverlays ? "primary" : "default"}
            >
              {showOverlays ? <VisibilityIcon /> : <VisibilityOffIcon />}
            </IconButton>
          </Tooltip>
        </Paper>

        {/* PDF Canvas Viewer with react-pdf */}
        <PDFCanvasViewer
          pdfUrl={pdfUrl}
          scale={scale}
          onPageRenderSuccess={(info) => {
            console.log(`✅ Page ${info.pageNumber} rendered:`, {
              pdfSize: `${info.pageWidth}x${info.pageHeight} pts`,
              renderSize: `${info.renderWidth}x${info.renderHeight} px`,
              scale: info.scale
            });

            // Store page render info for coordinate transformation
            setPageRenderInfo(prev => {
              const updated = new Map(prev);
              updated.set(info.pageNumber, info);
              return updated;
            });
          }}
          renderOverlay={(pageNumber, pageInfo) => {
            console.log(`🔍 DEBUG: renderOverlay called for page ${pageNumber}`);
            console.log(`🔍 DEBUG: backendPageDimensions Map size:`, backendPageDimensions.size);
            console.log(`🔍 DEBUG: overlays array length:`, overlays.length);

            // Get backend dimensions for this page (optional now - only used for debug comparison)
            const backendDims = backendPageDimensions.get(pageNumber) || {
              width: 0,
              height: 0
            };

            // Log if backend dimensions are missing (not blocking anymore since we use react-pdf dimensions)
            if (backendDims.width === 0 || backendDims.height === 0) {
              console.info(`ℹ️  No backend dimensions for page ${pageNumber} - using react-pdf dimensions only`);
            }

            console.log(`🔍 DEBUG: backendDims for page ${pageNumber}:`, backendDims);

            // Filter overlays for this page
            const pageOverlays = overlays.filter(o => o.position?.page === pageNumber);

            console.log(`🔍 DEBUG: Filtered ${pageOverlays.length} overlays for page ${pageNumber}`);
            console.log(`🔍 DEBUG: Total overlays:`, overlays.length);
            console.log(`🔍 DEBUG: Overlays with positions:`, overlays.filter(o => o.position).length);
            console.log(`🔍 DEBUG: Overlays with page numbers:`, overlays.filter(o => o.position?.page).map(o => o.position?.page));

            if (pageOverlays.length === 0) {
              console.warn(`⚠️  No overlays to render for page ${pageNumber}`);
              return null;
            }

            console.log(`🎯 Rendering ${pageOverlays.length} overlays for page ${pageNumber}`);

            return (
              <>
                {/* Marker Mode - Small colored dots */}
                {showOverlays && displayMode === 'markers' && pageOverlays.map((overlay) => {
                  if (!overlay.position) return null;

                  // Ensure page number is set
                  const positionWithPage: Coordinates = {
                    ...overlay.position,
                    page: overlay.position.page || pageNumber
                  };

                  // 🔍 AUTO-DETECT & CONVERT: Absolute PDF points → Normalized 0-1 scale
                  // Handles backward compatibility with old absolute coordinates from database
                  const { coords: normalizedCoords, format: coordFormat } = convertToNormalized(
                    positionWithPage,
                    backendDims.width || pageInfo.pageWidth,
                    backendDims.height || pageInfo.pageHeight
                  );

                  // 🎯 NORMALIZED COORDINATE TRANSFORMATION (0-1 scale → screen pixels)
                  // Now we're guaranteed to have normalized coordinates (resolution-independent)
                  const screenCoords = normalizedToScreen(
                    normalizedCoords,        // Guaranteed normalized 0-1 coordinates
                    pageInfo.renderWidth,    // Actual rendered width in pixels
                    pageInfo.renderHeight    // Actual rendered height in pixels
                  );

                  // 🔬 DETAILED COORDINATE DEBUG (first marker only for clarity)
                  if (pageNumber === 1 && overlay === pageOverlays[0]) {
                    console.log('🔬 ===== COORDINATE FORMAT DETECTION =====');
                    console.log('  📝 Field:', overlay.field);
                    console.log('  💾 Value:', overlay.value);
                    console.log('');
                    console.log('  🔍 Detected Format:', coordFormat === 'absolute' ? '⚠️  ABSOLUTE (old format - converted)' : '✅ NORMALIZED (new format)');
                    if (coordFormat === 'absolute') {
                      console.log('  📐 Original Absolute Coordinates:');
                      console.log('     X:', positionWithPage.x.toFixed(2), 'points (PDF absolute)');
                      console.log('     Y:', positionWithPage.y.toFixed(2), 'points (PDF absolute)');
                      console.log('     Width:', positionWithPage.width.toFixed(2), 'points');
                      console.log('     Height:', positionWithPage.height.toFixed(2), 'points');
                      console.log('');
                    }
                    console.log('  📐 Normalized Coordinates (0-1 scale):');
                    console.log('     X:', normalizedCoords.x.toFixed(4), '(fraction from left)');
                    console.log('     Y:', normalizedCoords.y.toFixed(4), '(fraction from BOTTOM)');
                    console.log('     Width:', normalizedCoords.width.toFixed(4), '(fraction of page width)');
                    console.log('     Height:', normalizedCoords.height.toFixed(4), '(fraction of page height)');
                    console.log('');
                    console.log('  📄 Rendering Info:');
                    console.log('     Canvas render size:', pageInfo.renderWidth, 'x', pageInfo.renderHeight, 'px');
                    console.log('     Backend PDF dimensions:', backendDims.width > 0 ? `${backendDims.width} x ${backendDims.height} points` : 'N/A');
                    console.log('');
                    console.log('  🎯 Final Screen Position:');
                    console.log('     X:', screenCoords.x.toFixed(2), 'px (from left)');
                    console.log('     Y:', screenCoords.y.toFixed(2), 'px (from TOP)');
                    console.log('     Width:', screenCoords.width.toFixed(2), 'px');
                    console.log('     Height:', screenCoords.height.toFixed(2), 'px');
                    console.log('  ✅ Transformation: normalized * renderSize = screen pixels');
                    console.log('🔬 ========================================');
                  }

                  return (
                    <Box
                      key={overlay.field}
                      data-field={overlay.field}
                      onClick={(e) => handleMarkerClick(overlay, e)}
                      onMouseEnter={() => setHoveredField(overlay.field)}
                      onMouseLeave={() => setHoveredField(null)}
                      sx={{
                        position: 'absolute',
                        left: `${screenCoords.x}px`,
                        top: `${screenCoords.y}px`,
                        width: `${MARKER_SIZE}px`,
                        height: `${MARKER_SIZE}px`,
                        backgroundColor: overlay.color.replace('0.3', '0.8'),
                        border: `2px solid ${overlay.color.replace('0.3', '1')}`,
                        borderRadius: '50%',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        zIndex: hoveredField === overlay.field ? 1000 : selectedMarker?.field === overlay.field ? 999 : 1,
                        transform: hoveredField === overlay.field ? 'scale(1.5)' : 'scale(1)',
                        boxShadow: hoveredField === overlay.field
                          ? '0 2px 8px rgba(0,0,0,0.3)'
                          : selectedMarker?.field === overlay.field
                          ? '0 0 0 3px rgba(66, 165, 245, 0.5)'
                          : 'none',
                        '&:hover': {
                          transform: 'scale(1.5)',
                          zIndex: 1000,
                        }
                      }}
                    >
                      {/* Mini tooltip on hover */}
                      {hoveredField === overlay.field && (
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: '100%',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            mb: 0.5,
                            bgcolor: 'rgba(0, 0, 0, 0.8)',
                            color: 'white',
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            fontSize: '11px',
                            whiteSpace: 'nowrap',
                            pointerEvents: 'none',
                            zIndex: 2000,
                          }}
                        >
                          {overlay.field.split('.').pop()?.replace(/_/g, ' ')}
                        </Box>
                      )}
                    </Box>
                  );
                })}

                {/* Overlay Mode - Full overlay boxes */}
                {showOverlays && displayMode === 'overlays' && pageOverlays.map((overlay) => {
                  if (!overlay.position) return null;

                  // Ensure page number is set
                  const positionWithPage: Coordinates = {
                    ...overlay.position,
                    page: overlay.position.page || pageNumber
                  };

                  // 🔍 AUTO-DETECT & CONVERT: Absolute PDF points → Normalized 0-1 scale
                  const { coords: normalizedCoords } = convertToNormalized(
                    positionWithPage,
                    backendDims.width || pageInfo.pageWidth,
                    backendDims.height || pageInfo.pageHeight
                  );

                  // 🎯 NORMALIZED COORDINATE TRANSFORMATION (0-1 scale → screen pixels)
                  const screenCoords = normalizedToScreen(
                    normalizedCoords,        // Guaranteed normalized 0-1 coordinates
                    pageInfo.renderWidth,    // Actual rendered width in pixels
                    pageInfo.renderHeight    // Actual rendered height in pixels
                  );

                  return (
                    <Tooltip
                      key={overlay.field}
                      title={
                        <Box>
                          <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                            {overlay.field.replace(/_/g, ' ').toUpperCase()}
                          </Typography>
                          <Typography variant="caption" display="block">
                            {typeof overlay.value === 'object'
                              ? JSON.stringify(overlay.value)
                              : overlay.value}
                          </Typography>
                          {overlay.confidence && (
                            <Typography variant="caption" display="block">
                              Confidence: {Math.round(overlay.confidence * 100)}%
                            </Typography>
                          )}
                        </Box>
                      }
                      placement="top"
                      arrow
                    >
                      <Box
                        data-field={overlay.field}
                        onClick={() => handleFieldSelect(overlay.field, overlay.value)}
                        onMouseEnter={() => setHoveredField(overlay.field)}
                        onMouseLeave={() => setHoveredField(null)}
                        sx={{
                          position: 'absolute',
                          left: `${screenCoords.x}px`,
                          top: `${screenCoords.y}px`,
                          width: `${screenCoords.width}px`,
                          height: `${screenCoords.height}px`,
                          backgroundColor: overlay.color,
                          border: `2px solid ${overlay.color.replace('0.3', '0.8')}`,
                          borderRadius: '4px',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          opacity: hoveredField === overlay.field || selectedField === overlay.field ? 1 : 0.7,
                          transform: hoveredField === overlay.field ? 'scale(1.05)' : 'scale(1)',
                          zIndex: selectedField === overlay.field ? 1000 : hoveredField === overlay.field ? 999 : 1,
                          '&:hover': {
                            opacity: 1,
                            boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
                          },
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'white',
                            backgroundColor: overlay.color.replace('0.3', '0.9'),
                            padding: '2px 6px',
                            borderRadius: '3px',
                            fontSize: '10px',
                            fontWeight: 'bold',
                            textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                          }}
                        >
                          {getFieldTypeIcon(overlay.type)} {overlay.field.substring(0, 15)}
                        </Typography>
                      </Box>
                    </Tooltip>
                  );
                })}
              </>
            );
          }}
        />

        {/* Field Details Popup */}
        {selectedMarker && popupPosition && (
          <Box
            sx={{
              position: 'absolute',
              left: `${popupPosition.x}px`,
              top: `${popupPosition.y}px`,
              zIndex: 2000,
            }}
          >
            <Fade in={Boolean(selectedMarker)} timeout={200}>
              <Paper
                elevation={8}
                sx={{
                  p: 2,
                  minWidth: 300,
                  maxWidth: 400,
                  bgcolor: 'background.paper',
                  border: 2,
                  borderColor: selectedMarker.color.replace('0.3', '1'),
                }}
              >
                {/* Close button */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                    FIELD DETAILS
                  </Typography>
                  <IconButton size="small" onClick={handleClosePopup} sx={{ mt: -1, mr: -1 }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>

                {/* Field icon and type */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="h6" sx={{ fontSize: 24 }}>
                    {getFieldTypeIcon(selectedMarker.type)}
                  </Typography>
                  <Chip
                    label={selectedMarker.type.toUpperCase()}
                    size="small"
                    sx={{ bgcolor: selectedMarker.color.replace('0.3', '0.2') }}
                  />
                </Box>

                {/* Field name */}
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Field Name
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', fontFamily: 'monospace' }}>
                    {selectedMarker.field}
                  </Typography>
                </Box>

                {/* Field value */}
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Value
                  </Typography>
                  <Paper sx={{ p: 1, bgcolor: 'grey.50', mt: 0.5 }}>
                    <Typography variant="body1" sx={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                      {(() => {
                        // Smart value truncation to prevent overwhelming popups
                        if (typeof selectedMarker.value === 'object') {
                          const json = JSON.stringify(selectedMarker.value, null, 2);
                          // Truncate JSON if > 500 characters
                          if (json.length > 500) {
                            return json.substring(0, 500) + '\n\n... (truncated - full value too long to display)';
                          }
                          return json;
                        }

                        // Handle string values
                        const strValue = String(selectedMarker.value || '-');
                        // Truncate long strings if > 200 characters
                        if (strValue.length > 200) {
                          return strValue.substring(0, 200) + '... (truncated)';
                        }
                        return strValue;
                      })()}
                    </Typography>
                  </Paper>
                </Box>

                {/* Action buttons */}
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ContentCopyIcon />}
                    onClick={() => {
                      navigator.clipboard.writeText(String(selectedMarker.value));
                      toast.success('Value copied to clipboard!');
                    }}
                    sx={{ flexGrow: 1 }}
                  >
                    Copy
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleClosePopup}
                  >
                    Close
                  </Button>
                </Box>
              </Paper>
            </Fade>
          </Box>
        )}
      </Box>

      {/* Extracted Fields Panel */}
      <Paper
        sx={{
          width: 350,
          p: 2,
          overflow: 'auto',
          borderLeft: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="h6" gutterBottom>
          Extracted Fields ({overlays.length})
        </Typography>

        {/* Legend */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
          <Chip size="small" label="📅 Dates" sx={{ bgcolor: 'rgba(156, 39, 176, 0.1)' }} />
          <Chip size="small" label="💰 Amounts" sx={{ bgcolor: 'rgba(76, 175, 80, 0.1)' }} />
          <Chip size="small" label="🔢 References" sx={{ bgcolor: 'rgba(255, 152, 0, 0.1)' }} />
          <Chip size="small" label="📍 Addresses" sx={{ bgcolor: 'rgba(0, 188, 212, 0.1)' }} />
        </Box>

        {/* Field List */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {overlays.map((overlay, index) => (
            <Zoom in={true} style={{ transitionDelay: `${index * 50}ms` }} key={overlay.field}>
              <Paper
                elevation={selectedField === overlay.field ? 3 : 1}
                sx={{
                  p: 1.5,
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  borderLeft: 4,
                  borderColor: overlay.color.replace('0.3', '1'),
                  bgcolor: selectedField === overlay.field ? 'action.selected' : 'background.paper',
                  '&:hover': {
                    elevation: 3,
                    bgcolor: 'action.hover',
                  },
                }}
                onClick={() => {
                  handleFieldSelect(overlay.field, overlay.value);
                  // Scroll to the field in the overlay if it has a position
                  if (overlay.position && overlayContainerRef.current) {
                    const element = overlayContainerRef.current.querySelector(
                      `[data-field="${overlay.field}"]`
                    );
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography variant="caption" sx={{ fontSize: 18 }}>
                    {getFieldTypeIcon(overlay.type)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }}>
                    {overlay.field.replace(/_/g, ' ').toUpperCase()}
                  </Typography>
                  {overlay.confidence && (
                    <Chip
                      size="small"
                      label={`${Math.round(overlay.confidence * 100)}%`}
                      color={overlay.confidence > 0.9 ? 'success' : overlay.confidence > 0.7 ? 'warning' : 'error'}
                    />
                  )}
                </Box>
                <Typography variant="body2" sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontFamily: overlay.type === 'amount' ? 'monospace' : 'inherit',
                }}>
                  {typeof overlay.value === 'object'
                    ? JSON.stringify(overlay.value, null, 2)
                    : overlay.value || '-'}
                </Typography>
              </Paper>
            </Zoom>
          ))}
        </Box>

        {overlays.length === 0 && (
          <Alert severity="info">
            No fields extracted yet. Process the document to see extracted data.
          </Alert>
        )}
      </Paper>
    </Box>
  );
};

export default PDFOverlayViewerV2;
